<template>
  <div class="comment-section">
    <!-- 评论列表 -->
    <div class="comment-list" v-if="comments.length > 0">
      <h3>评论列表</h3>
      <!-- 主评论 -->
      <div v-for="comment in mainComments" :key="comment.id" class="comment-item">
        <div class="comment-content">
          <div class="comment-header">
            <span class="nickname">{{ comment.nickname }}</span>
            <span class="time">{{ formatTime(comment.created_at) }}</span>
          </div>
          <div class="comment-body">{{ comment.content }}</div>
          <div v-if="isAdmin" class="comment-actions">
            <el-button type="text" size="small" @click="showReplyForm(comment)">回复</el-button>
            <el-button type="text" size="small" @click="handleDelete(comment.id)">删除</el-button>
          </div>
        </div>
        <!-- 回复评论 -->
        <div v-if="getReplyComments(comment.id).length > 0" class="reply-list">
          <div v-for="reply in getReplyComments(comment.id)"
               :key="reply.id"
               class="reply-item">
            <div class="comment-content">
              <div class="comment-header">
                <span class="nickname">{{ reply.nickname }}</span>
                <span class="reply-to">回复</span>
                <span class="nickname">{{ comment.nickname }}</span>
                <span class="time">{{ formatTime(reply.created_at) }}</span>
              </div>
              <div class="comment-body">{{ reply.content }}</div>
              <div v-if="isAdmin" class="comment-actions">
                <el-button type="text" size="small" @click="handleDelete(reply.id)">删除</el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="no-comments">暂无评论</div>

    <!-- 评论表单 - 只在非管理员场景下显示 -->
    <div v-if="!isAdmin" class="comment-form">
      <h3>发表评论</h3>
      <form @submit.prevent="submitComment">
        <div class="form-item">
          <label for="comment-nickname">昵称：</label>
          <input
            id="comment-nickname"
            type="text"
            v-model="form.nickname"
            placeholder="请输入昵称"
            autocomplete="nickname"
            required
          />
        </div>
        <div class="form-item">
          <label for="comment-email">邮箱：</label>
          <input
            id="comment-email"
            type="email"
            v-model="form.email"
            placeholder="请输入邮箱"
            autocomplete="email"
            required
          />
        </div>
        <div class="form-item">
          <label for="comment-content">评论内容：</label>
          <textarea
            id="comment-content"
            v-model="form.content"
            rows="4"
            placeholder="请输入评论内容"
            required
          ></textarea>
        </div>
        <div class="form-item">
          <button type="submit" class="submit-btn">发表评论</button>
        </div>
      </form>
    </div>

    <!-- 回复对话框 -->
    <div v-if="replyDialogVisible" class="reply-dialog" role="presentation">
      <div class="reply-dialog-content" role="dialog" aria-modal="true" aria-labelledby="reply-dialog-title">
        <h3 id="reply-dialog-title">回复评论</h3>
        <form @submit.prevent="submitReply">
          <div class="form-item">
            <label for="reply-content">回复内容：</label>
            <textarea
              id="reply-content"
              v-model="replyForm.content"
              rows="4"
              placeholder="请输入回复内容"
              required
            ></textarea>
          </div>
          <div class="form-actions">
            <button type="button" @click="replyDialogVisible = false">取消</button>
            <button type="submit">确定</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { getComments, addComment, replyComment, deleteComment } from '@/api/comment'
import { formatTime } from '@/utils'

export default {
  name: 'Comment',
  props: {
    articleId: {
      type: Number,
      required: true
    },
    isAdmin: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      comments: [],
      form: {
        nickname: '',
        email: '',
        content: ''
      },
      formErrors: {
        email: ''
      },
      replyDialogVisible: false,
      replyForm: {
        content: ''
      },
      currentComment: null
    }
  },
  created() {
    // 移除这里的直接调用，因为 watch 会处理
    // if (this.articleId) {
    //   this.fetchComments()
    // }
  },
  watch: {
    articleId: {
      immediate: true,  // 保持 immediate: true，这样组件创建时会自动调用一次
      handler(newVal) {
        if (newVal) {
          this.fetchComments()
        }
      }
    }
  },
  computed: {
    // 获取主评论（非回复的评论）
    mainComments() {
      return this.comments.filter(comment => !comment.is_reply)
    }
  },
  methods: {
    async fetchComments() {
      if (!this.articleId) return

      try {
        const { data } = await getComments(this.articleId)
        this.comments = data || []
      } catch (error) {
        this.$message.error('获取评论列表失败')
        console.error('获取评论失败:', error)
      }
    },
    validateEmail(email) {
      const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      return re.test(email)
    },
    async submitComment() {
      try {
        // 验证邮箱格式
        if (!this.validateEmail(this.form.email)) {
          this.$message.error('请输入正确的邮箱格式')
          return
        }

        const data = {
          ...this.form,
          article_id: this.articleId
        }
        await addComment(data)
        this.$message.success('评论成功')
        // 重置表单
        this.form.nickname = ''
        this.form.email = ''
        this.form.content = ''
        this.formErrors.email = ''
        this.fetchComments()
      } catch (error) {
        if (error.message) {
          this.$message.error(error.message)
          console.error('提交评论失败:', error)
        }
      }
    },
    showReplyForm(comment) {
      console.log('当前评论:', comment)
      this.currentComment = comment
      this.replyDialogVisible = true
    },
    async submitReply() {
      try {
        if (!this.currentComment || !this.currentComment.id) {
          throw new Error('评论ID无效')
        }
        const data = {
          content: this.replyForm.content,
          parent_id: this.currentComment.id
        }
        await replyComment(this.currentComment.id, data)
        this.$message.success('回复成功')
        this.replyDialogVisible = false
        this.replyForm.content = ''
        this.fetchComments()
      } catch (error) {
        this.$message.error(error.message || '回复失败')
        console.error('回复评论失败:', error)
      }
    },
    async handleDelete(commentId) {
      try {
        await this.$confirm('确认删除该评论吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await deleteComment(commentId)
        this.$message.success('删除成功')
        this.fetchComments()
      } catch (error) {
        if (error !== 'cancel' && error.message) {
          this.$message.error(error.message)
          console.error('删除评论失败:', error)
        }
      }
    },
    formatTime,
    // 获取某条评论的回复列表
    getReplyComments(parentId) {
      return this.comments.filter(comment =>
        comment.is_reply && comment.parent_id === parentId
      )
    }
  }
}
</script>

<style scoped>
.comment-section {
  margin-top: 40px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
}

.comment-section h3 {
  font-size: 1.5rem;
  color: #2c3e50;
  margin-bottom: 1.5rem;
  font-weight: 600;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.comment-list {
  margin-bottom: 2rem;
}

.comment-item {
  padding: 1.5rem;
  border-bottom: 1px solid #edf2f7;
  transition: all 0.3s ease;
  background-color: #fff;
}

.comment-item:hover {
  background-color: rgba(247, 250, 252, 0.5);
  transform: translateX(4px);
}

.comment-content {
  position: relative;
}

.comment-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
}

.nickname {
  font-weight: 600;
  color: #2d3748;
  margin-right: 0.75rem;
  font-size: 0.95rem;
}

.time {
  color: #718096;
  font-size: 0.85rem;
}

.comment-body {
  line-height: 1.7;
  color: #4a5568;
  font-size: 0.95rem;
  margin-bottom: 0.75rem;
  word-break: break-word;
}

.comment-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.comment-actions button {
  font-size: 0.85rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.comment-actions button:hover {
  background-color: #f7fafc;
}

.reply-list {
  margin: 1rem 0 0 2rem;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 8px;
}

.reply-item {
  background-color: #f8fafc;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.reply-item:hover {
  background-color: rgba(237, 242, 247, 0.7);
  transform: translateX(4px);
}

.reply-to {
  color: #718096;
  margin: 0 0.5rem;
  font-size: 0.85rem;
}

.no-comments {
  text-align: center;
  padding: 3rem 0;
  color: #718096;
  font-size: 0.95rem;
  background-color: #f8fafc;
  border-radius: 8px;
  margin: 1rem 0;
}

/* 评论表单样式 */
.comment-form {
  max-width: 100%;
  background-color: #fff;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-top: 2rem;
}

.form-item {
  margin-bottom: 1.25rem;
}

.form-item label {
  display: block;
  margin-bottom: 0.5rem;
  color: #4a5568;
  font-size: 0.95rem;
  font-weight: 500;
}

.form-item input,
.form-item textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background-color: #f8fafc;
}

.form-item input:focus,
.form-item textarea:focus {
  outline: none;
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  background-color: #fff;
}

.submit-btn {
  background-color: #4299e1;
  color: #fff;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
}

.submit-btn:hover {
  background-color: #3182ce;
  transform: translateY(-1px);
}

.submit-btn:active {
  transform: translateY(0);
}

/* 回复对话框样式 */
.reply-dialog {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.reply-dialog-content {
  background-color: #fff;
  padding: 2rem;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}

.form-actions button {
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
}

.form-actions button:first-child {
  background-color: #e2e8f0;
  color: #4a5568;
}

.form-actions button:last-child {
  background-color: #4299e1;
  color: #fff;
}

.form-actions button:hover {
  transform: translateY(-1px);
}

.form-actions button:first-child:hover {
  background-color: #cbd5e0;
}

.form-actions button:last-child:hover {
  background-color: #3182ce;
}

@media (max-width: 640px) {
  .comment-section {
    margin-top: 1.5rem;
  }

  .reply-list {
    margin-left: 1rem;
  }

  .comment-form,
  .reply-dialog-content {
    padding: 1rem;
  }

  .form-actions {
    flex-direction: column;
  }

  .form-actions button {
    width: 100%;
  }
}
</style>
